import { ChangeEvent, FC, PointerEvent, useRef, useState } from 'react';

import { Box, Button, Flex, HStack, IconButton, Input, rem, Tooltip } from '@bonusly/design-system';

import type { DoodleTranslate } from './doodle-config';

interface HsvColor {
	h: number;
	s: number;
	v: number;
}

interface DoodleCustomColorPickerProps {
	initialColor: string;
	onCancel: () => void;
	onAdd: (color: string) => void;
	translate: DoodleTranslate;
}

interface EyeDropperResult {
	sRGBHex: string;
}

interface EyeDropperApi {
	open: () => Promise<EyeDropperResult>;
}

interface EyeDropperConstructor {
	new (): EyeDropperApi;
}

const isHexColor = (color: string): boolean => /^#[0-9A-F]{6}$/i.test(color);
const normalizeHex = (color: string): string => (color.startsWith('#') ? color : `#${color}`).toUpperCase();
const clamp = (value: number): number => Math.min(1, Math.max(0, value));

const hexToHsv = (hex: string): HsvColor => {
	const red = parseInt(hex.slice(1, 3), 16) / 255;
	const green = parseInt(hex.slice(3, 5), 16) / 255;
	const blue = parseInt(hex.slice(5, 7), 16) / 255;
	const max = Math.max(red, green, blue);
	const min = Math.min(red, green, blue);
	const delta = max - min;

	let hue = 0;
	if (delta > 0) {
		if (max === red) hue = 60 * (((green - blue) / delta) % 6);
		else if (max === green) hue = 60 * ((blue - red) / delta + 2);
		else hue = 60 * ((red - green) / delta + 4);
	}

	return {
		h: hue < 0 ? hue + 360 : hue,
		s: max === 0 ? 0 : delta / max,
		v: max,
	};
};

const hsvToHex = ({ h, s, v }: HsvColor): string => {
	const chroma = v * s;
	const section = h / 60;
	const x = chroma * (1 - Math.abs((section % 2) - 1));
	const offset = v - chroma;

	let red = 0;
	let green = 0;
	let blue = 0;

	if (section < 1) [red, green, blue] = [chroma, x, 0];
	else if (section < 2) [red, green, blue] = [x, chroma, 0];
	else if (section < 3) [red, green, blue] = [0, chroma, x];
	else if (section < 4) [red, green, blue] = [0, x, chroma];
	else if (section < 5) [red, green, blue] = [x, 0, chroma];
	else [red, green, blue] = [chroma, 0, x];

	const toHex = (channel: number) =>
		Math.round((channel + offset) * 255)
			.toString(16)
			.padStart(2, '0');

	return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
};

const EyedropperIcon: FC = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path
			d="M17.1028 10.6097L17.5603 11.0682C17.6996 11.2075 17.8102 11.3729 17.8856 11.5549C17.961 11.7369 17.9998 11.932 17.9998 12.129C17.9998 12.326 17.961 12.5211 17.8856 12.7031C17.8102 12.8851 17.6996 13.0505 17.5603 13.1897L16.7166 14.0335C16.5759 14.174 16.3852 14.253 16.1864 14.253C15.9876 14.253 15.7969 14.174 15.6562 14.0335L9.96937 8.34381C9.82883 8.20317 9.74988 8.01248 9.74988 7.81365C9.74988 7.61483 9.82883 7.42414 9.96937 7.2835L10.8131 6.43975C10.9524 6.30042 11.1178 6.1899 11.2998 6.1145C11.4818 6.0391 11.6769 6.00029 11.8739 6.00029C12.0709 6.00029 12.266 6.0391 12.448 6.1145C12.63 6.1899 12.7954 6.30042 12.9347 6.43975L13.3931 6.89725L15.7369 4.54412C16.7484 3.53256 18.3966 3.47069 19.4344 4.45037C19.6895 4.69141 19.8937 4.98116 20.0348 5.30249C20.176 5.62381 20.2513 5.97018 20.2563 6.32112C20.2613 6.67205 20.1958 7.02042 20.0638 7.34562C19.9318 7.67082 19.736 7.96625 19.4878 8.21444L17.1028 10.6097Z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M14.9353 13.3069L9.62157 18.6216C9.23622 19.0082 8.75268 19.2824 8.22305 19.4146C7.69341 19.5467 7.13776 19.5319 6.61594 19.3716L4.74094 20.191C4.6028 20.2514 4.44965 20.2688 4.30147 20.2409C4.1533 20.2129 4.01699 20.141 3.91032 20.0344C3.83377 19.9579 3.78209 19.86 3.76202 19.7537C3.74195 19.6473 3.75442 19.5373 3.79782 19.4382L4.65938 17.4638C4.48052 16.9339 4.45333 16.3646 4.58088 15.8201C4.70844 15.2756 4.98566 14.7775 5.38125 14.3822L10.6959 9.06848"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export const DoodleCustomColorPicker: FC<DoodleCustomColorPickerProps> = ({ initialColor, onCancel, onAdd, translate: t }) => {
	const normalizedInitialColor = isHexColor(initialColor) ? initialColor.toUpperCase() : '#14151A';
	const [hsv, setHsv] = useState<HsvColor>(() => hexToHsv(normalizedInitialColor));
	const [hex, setHex] = useState(normalizedInitialColor);
	const isDraggingRef = useRef(false);

	const updateColor = (nextHsv: HsvColor) => {
		setHsv(nextHsv);
		setHex(hsvToHex(nextHsv));
	};

	const updateSaturationAndValue = (event: PointerEvent<HTMLDivElement>) => {
		const bounds = event.currentTarget.getBoundingClientRect();
		updateColor({
			h: hsv.h,
			s: clamp((event.clientX - bounds.left) / bounds.width),
			v: 1 - clamp((event.clientY - bounds.top) / bounds.height),
		});
	};

	const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
		isDraggingRef.current = true;
		event.currentTarget.setPointerCapture(event.pointerId);
		updateSaturationAndValue(event);
	};

	const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
		if (isDraggingRef.current) updateSaturationAndValue(event);
	};

	const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
		isDraggingRef.current = false;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
	};

	const handleHexChange = (nextHex: string) => {
		const normalized = normalizeHex(nextHex);
		setHex(nextHex.toUpperCase());
		if (isHexColor(normalized)) setHsv(hexToHsv(normalized));
	};

	const handleEyedropper = async () => {
		const EyeDropper = (window as typeof window & { EyeDropper?: EyeDropperConstructor }).EyeDropper;
		if (!EyeDropper) return;

		try {
			const result = await new EyeDropper().open();
			const nextHex = result.sRGBHex.toUpperCase();
			setHex(nextHex);
			setHsv(hexToHsv(nextHex));
		} catch {
			// Closing the system eyedropper without choosing a color is expected.
		}
	};

	const canUseEyedropper = typeof window !== 'undefined' && 'EyeDropper' in window;
	const normalizedHex = normalizeHex(hex);
	const isValidHex = isHexColor(normalizedHex);

	return (
		<Box width={rem(320)}>
			<Box
				position="relative"
				height={rem(160)}
				borderRadius="xl"
				overflow="hidden"
				cursor="crosshair"
				bg={`linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h} 100% 50%))`}
				sx={{ touchAction: 'none' }}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerUp}
			>
				<Box
					position="absolute"
					left={`${hsv.s * 100}%`}
					top={`${(1 - hsv.v) * 100}%`}
					width={rem(18)}
					height={rem(18)}
					borderRadius="full"
					borderWidth={rem(3)}
					borderStyle="solid"
					borderColor="bg.surface"
					boxShadow="md"
					transform="translate(-50%, -50%)"
					pointerEvents="none"
				/>
			</Box>

			<Box
				as="input"
				type="range"
				aria-label={t('doodle.colors.hue')}
				min={0}
				max={359}
				value={Math.round(hsv.h)}
				onChange={(event: ChangeEvent<HTMLInputElement>) => updateColor({ ...hsv, h: Number(event.target.value) })}
				width="100%"
				height={rem(28)}
				mt={3}
				cursor="pointer"
				bg="linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)"
				borderRadius="full"
				sx={{
					appearance: 'none',
					'&::-webkit-slider-thumb': {
						appearance: 'none',
						width: rem(22),
						height: rem(22),
						borderRadius: '50%',
						background: hsvToHex({ h: hsv.h, s: 1, v: 1 }),
						border: `${rem(3)} solid var(--chakra-colors-bg-surface)`,
						boxShadow: 'var(--chakra-shadows-md)',
					},
					'&::-moz-range-thumb': {
						width: rem(22),
						height: rem(22),
						borderRadius: '50%',
						background: hsvToHex({ h: hsv.h, s: 1, v: 1 }),
						border: `${rem(3)} solid var(--chakra-colors-bg-surface)`,
						boxShadow: 'var(--chakra-shadows-md)',
					},
				}}
			/>

			<HStack mt={3} spacing={2}>
				<Flex flex={1} align="center" gap={2} borderWidth={rem(1)} borderStyle="solid" borderColor="border.default" borderRadius="lg" px={3} height={rem(48)}>
					<Box width={rem(28)} height={rem(28)} flexShrink={0} borderRadius="full" bg={isValidHex ? normalizedHex : 'transparent'} />
					<Input
						value={hex}
						onChange={(event) => handleHexChange(event.target.value)}
						onBlur={() => {
							if (!isValidHex) setHex(hsvToHex(hsv));
						}}
						aria-label={t('doodle.colors.hex')}
						variant="unstyled"
						fontFamily="mono"
						maxLength={7}
					/>
				</Flex>
				<Tooltip label={t('doodle.colors.eyedropper')} placement="top">
					<IconButton
						aria-label={t('doodle.colors.eyedropper')}
						icon={<EyedropperIcon />}
						variant="outline"
						colorScheme="secondary"
						height={rem(48)}
						width={rem(48)}
						isDisabled={!canUseEyedropper}
						onClick={() => void handleEyedropper()}
					/>
				</Tooltip>
			</HStack>

			<HStack justify="flex-end" mt={4} spacing={2}>
				<Button variant="ghost" colorScheme="secondary" size="sm" onClick={onCancel}>
					{t('doodle.cancel')}
				</Button>
				<Button colorScheme="primary" size="sm" isDisabled={!isValidHex} onClick={() => onAdd(normalizedHex)}>
					{t('doodle.colors.confirm_add')}
				</Button>
			</HStack>
		</Box>
	);
};
